import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const RegistrarFirmaAcudiente = () => {
  // 👇 ahora recibes ambos IDs desde la URL
  const { idAprendiz, idAcudiente } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const [mensaje, setMensaje] = useState("");

  // Límites para escritorio
  const MAX_DESKTOP_WIDTH = 700;
  const DESKTOP_HEIGHT = 260;

  // ========= Utils: Bounding Box + Crop PNG =========
  const getStrokeBoundingBox = (canvas, alphaThreshold = 1) => {
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const { data } = ctx.getImageData(0, 0, width, height);

    let top = height, left = width, right = -1, bottom = -1;
    for (let y = 0; y < height; y++) {
      const rowStart = y * width * 4;
      for (let x = 0; x < width; x++) {
        const idx = rowStart + x * 4;
        const a = data[idx + 3]; // alpha
        if (a >= alphaThreshold) {
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
    }
    if (right === -1) return null; // no hay trazo
    return { left, top, right, bottom };
  };

  const getCroppedSignatureBlob = async (
    canvas,
    { padding = 12, normalizeDPR = true, mime = "image/png", quality = 0.92, alphaThreshold = 1 } = {}
  ) => {
    const bbox = getStrokeBoundingBox(canvas, alphaThreshold);
    if (!bbox) return null;

    const dpr = window.devicePixelRatio || 1;
    const srcX = Math.max(0, bbox.left - padding);
    const srcY = Math.max(0, bbox.top - padding);
    const srcW = Math.min(canvas.width - srcX, (bbox.right - bbox.left + 1) + padding * 2);
    const srcH = Math.min(canvas.height - srcY, (bbox.bottom - bbox.top + 1) + padding * 2);

    const outW = normalizeDPR ? Math.max(1, Math.round(srcW / dpr)) : srcW;
    const outH = normalizeDPR ? Math.max(1, Math.round(srcH / dpr)) : srcH;

    const off = document.createElement("canvas");
    off.width = outW;
    off.height = outH;
    const offCtx = off.getContext("2d");
    offCtx.imageSmoothingEnabled = true;
    offCtx.imageSmoothingQuality = "high";
    offCtx.drawImage(canvas, srcX, srcY, srcW, srcH, 0, 0, outW, outH);

    return await new Promise((resolve) => off.toBlob(resolve, mime, quality));
  };

  // ========= Responsive Canvas =========
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsPortrait(window.innerHeight > window.innerWidth);

      if (mobile) {
        const width = window.innerWidth;
        const height = window.innerHeight * 0.75;
        canvas.width = Math.max(1, Math.floor(width * ratio));
        canvas.height = Math.max(1, Math.floor(height * ratio));
        canvas.style.width = width + "px";
        canvas.style.height = height + "px";
      } else {
        const parentWidth = canvas.parentElement?.offsetWidth || MAX_DESKTOP_WIDTH;
        const visibleWidth = Math.min(parentWidth * 0.9, MAX_DESKTOP_WIDTH);
        const visibleHeight = DESKTOP_HEIGHT;
        canvas.width = Math.max(1, Math.floor(visibleWidth * ratio));
        canvas.height = Math.max(1, Math.floor(visibleHeight * ratio));
        canvas.style.width = `${visibleWidth}px`;
        canvas.style.height = `${visibleHeight}px`;
      }

      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  // ========= Dibujo =========
  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e.clientX ?? 0) - rect.left, y: (e.clientY ?? 0) - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPosition(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const endDrawing = (e) => {
    e.preventDefault();
    setDrawing(false);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPosition(e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // ========= Guardar firma de ACUDIENTE (por idAcudiente) =========
  const saveSignature = async () => {
    const canvas = canvasRef.current;

    try {
      const blob = await getCroppedSignatureBlob(canvas, {
        padding: 12,
        normalizeDPR: true,
        mime: "image/png",
        quality: 0.92,
        alphaThreshold: 1,
      });

      if (!blob) {
        setMensaje("⚠️ No hay firma dibujada.");
        return;
      }

      const file = new File(
        [blob],
        `firmaAcudiente-${idAprendiz}-${idAcudiente}.png`,
        { type: "image/png" }
      );

      const formData = new FormData();
      // 👇 coincide con tu middleware: uploadFirmasAcudiente.single("firmaAcudiente")
      formData.append("firmaAcudiente", file);

      await axios.put(
        `/api/aprendices/update/firma-acudiente/${idAprendiz}/${idAcudiente}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setMensaje("✅ Firma del acudiente guardada con éxito");
      setTimeout(() => navigate(-1), 1200);
    } catch (error) {
      console.error("Error al guardar firma:", error);
      setMensaje("❌ Error al guardar la firma del acudiente");
    }
  };

  return (
    <div
      className={isMobile ? "" : "card shadow p-3 mb-3"}
      style={{
        height: isMobile ? "100vh" : "auto",
        width: isMobile ? "100vw" : "auto",
        position: isMobile ? "fixed" : "static",
        top: isMobile ? 0 : "auto",
        left: isMobile ? 0 : "auto",
        background: "#fff",
        zIndex: 1050,
        overflow: "hidden",
      }}
    >
      {!isMobile && (
        <h5 className="text-center mb-3">
          ✍️ Registrar Firma del <b>Acudiente</b> — Aprendiz: {idAprendiz}, Acudiente: {idAcudiente}
        </h5>
      )}

      {isMobile && isPortrait && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.8)",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            zIndex: 2000,
            textAlign: "center",
            padding: "20px",
          }}
        >
          <h3>🔄 Rota tu dispositivo</h3>
          <p>Por favor coloca el dispositivo en posición horizontal para firmar.</p>
        </div>
      )}

      <div className="d-flex justify-content-center">
        <div style={{ width: "100%", maxWidth: `${MAX_DESKTOP_WIDTH}px` }}>
          <canvas
            ref={canvasRef}
            className="border border-dark rounded"
            style={{
              backgroundColor: "#fff",
              cursor: "crosshair",
              touchAction: "none",
              display: "block",
              margin: "0 auto",
            }}
            onMouseDown={startDrawing}
            onMouseUp={endDrawing}
            onMouseMove={draw}
            onMouseLeave={endDrawing}
            onTouchStart={startDrawing}
            onTouchEnd={endDrawing}
            onTouchMove={draw}
          />
        </div>
      </div>

      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          position: isMobile ? "fixed" : "static",
          bottom: isMobile ? "15px" : "auto",
          left: isMobile ? "10px" : "auto",
          right: isMobile ? "10px" : "auto",
          background: isMobile ? "rgba(255,255,255,0.95)" : "none",
          padding: isMobile ? "4px" : "0",
          borderRadius: "12px",
          boxShadow: isMobile ? "0px 2px 6px rgba(0,0,0,0.2)" : "none",
          marginTop: "15px",
          gap: "8px",
          maxWidth: isMobile ? "unset" : `${MAX_DESKTOP_WIDTH}px`,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <button type="button" className="btn btn-secondary" onClick={clearCanvas}>
          Limpiar
        </button>
        <button type="button" className="btn btn-success" onClick={saveSignature}>
          Guardar Firma
        </button>
      </div>

      {mensaje && (
        <p className="text-center mt-3" style={{ color: "green", fontWeight: "bold" }}>
          {mensaje}
        </p>
      )}
    </div>
  );
};

export default RegistrarFirmaAcudiente;
